<script setup lang="ts">

import { ref, computed } from 'vue';
import { usePresentation } from '@/modules/Presentation/Types';
import { comma } from '@library/Utilities';

const presentation = usePresentation();

const numberOfRooftops = ref( 500 );
const penetration = ref( 25 );
const carsPerMonth = ref( 50 );
const margin = ref( 200 );
const shareOfRevenue = ref( 0.5 );

const handleNaN = ( v: number ) => isNaN( v ) ? 0 : Number( v );

const monthlyVehicleSales = computed( () => Math.ceil( handleNaN( numberOfRooftops.value * carsPerMonth.value ) ) );
const yearlyVehicleSales = computed( () => Math.ceil( handleNaN( monthlyVehicleSales.value * 12 ) ) );
const monthlyProtectSales = computed( () => Math.ceil( Math.ceil( handleNaN( monthlyVehicleSales.value * penetration.value / 100 ) ) ) );
const yearlyProtectSales = computed( () => Math.ceil( Math.ceil( handleNaN( monthlyProtectSales.value * 12 ) ) ) );
const monthlyProtectRevenue = computed( () => Math.ceil( Math.ceil( handleNaN( monthlyProtectSales.value * margin.value ) ) ) );
const yearlyProtectRevenue = computed( () => Math.ceil( Math.ceil( handleNaN( yearlyProtectSales.value * margin.value  ) ) ) );
const monthlyResellerRevenue = computed( () => Math.ceil( Math.ceil( handleNaN( monthlyProtectRevenue.value * shareOfRevenue.value ) ) ) );
const yearlyResellerRevenue = computed( () => Math.ceil( Math.ceil( handleNaN( yearlyProtectRevenue.value * shareOfRevenue.value  ) ) ) );

const formatNumber = (v:number, sign?: string ) => {
    v = handleNaN( v );
    if( v < 100000 ) return `${sign == null ? '' : sign}${comma( v, 0 )}`;
    if( v < 1000000 ) return `${sign == null ? '' : sign}${comma( (v/1000), 1 )}K`;
    return `${sign == null ? '' : sign}${comma( (v/1000000), 2 )}M`;
}

</script>

<template>

    <Page>
    
        <div class="bg-white rounded-lg text-gray-800 p-4 flex flex-col gap-2 w-full">

            <!-- <div class="bg-slate-100/40 w-[calc(30%+0.25rem)] h-full absolute top-0 right-0 rounded-r-lg"></div> -->
            <div class="grid grid-cols-10 items-center gap-x-4 border-b pb-2 mb-2">
                <div class="col-span-4 font-semibold text-xl"> 
                    Revenue Calculator
                </div>
                <div class="font-semibold">Variables</div>
                <div class="font-semibold text-right">Monthly</div>
                <div class="font-semibold text-right">Annually</div>
                <div class="font-semibold text-right">Year 1</div>
                <div class="font-semibold text-right">Year 2</div>
                <div class="font-semibold text-right">Year 3</div>
            </div>


            <div class="grid grid-cols-10 items-center gap-x-4">
                <div class="col-span-4">
                    Number of Rooftops Onboarded
                </div>
                <div class="bg-slate-600 rounded-md">
                    <TextField 
                        v-model="numberOfRooftops" 
                        density="compact"
                        variant="flat"
                        hide-details
                    />
                </div>
                <div class="p-1 h-10 flex items-center">
                </div>
                <div class="p-1 h-10 flex items-center">
                </div>
                <div class="justify-end bg-slate-100 rounded p-1 h-10 flex items-center">
                    {{ comma( Math.ceil( handleNaN( numberOfRooftops * 0.33 ) ), 0 ) }}
                </div>
                <div class="justify-end bg-slate-100 rounded p-1 h-10 flex items-center">
                    {{ comma( Math.ceil( handleNaN( numberOfRooftops * 0.66 ) ), 0 ) }}
                </div>
                <div class="justify-end bg-slate-100 rounded p-1 h-10 flex items-center">{{ comma( handleNaN( numberOfRooftops ), 0 ) }}</div>
            </div>


            <div class="grid grid-cols-10 items-center gap-x-4">
                <div class="col-span-4">
                    Vehicle Sales per Rooftop per Month
                </div>
                <div class="bg-slate-600 rounded-md">
                    <TextField 
                        v-model="carsPerMonth" 
                        density="compact"
                        variant="flat"
                        hide-details
                    />
                </div>
                <div class="p-1 h-10 flex items-center">
                </div>
                <div class="p-1 h-10 flex items-center">
                </div>
                <div class="rounded p-1 h-10 flex items-center justify-center">
                    <div class="w-0.5 h-[150%] bg-slate-300"></div>
                </div>
                <div class="rounded p-1 h-10 flex items-center justify-center">
                    <div class="w-0.5 h-[150%] bg-slate-300"></div>
                </div>
                <div class="rounded p-1 h-10 flex items-center justify-center">
                    <div class="w-0.5 h-[150%] bg-slate-300"></div>
                </div>
            </div>


            <div class="grid grid-cols-10 items-center gap-x-4">
                <div class="col-span-4">
                    Penetration
                </div>
                <div class="bg-slate-600 rounded-md">
                    <TextField 
                        v-model="penetration" 
                        density="compact"
                        variant="flat"
                        hide-details>
                        <template #append-inner>%</template>
                    </TextField>
                </div>
                <div class="p-1 h-10 flex items-center">
                </div>
                <div class="p-1 h-10 flex items-center">
                </div>
                <div class="rounded p-1 h-10 flex items-center justify-center">
                    <div class="w-0.5 h-[150%] bg-slate-300"></div>
                </div>
                <div class="rounded p-1 h-10 flex items-center justify-center">
                    <div class="w-0.5 h-[150%] bg-slate-300"></div>
                </div>
                <div class="rounded p-1 h-10 flex items-center justify-center">
                    <div class="w-0.5 h-[150%] bg-slate-300"></div>
                </div>
            </div>


            <div class="grid grid-cols-10 items-center gap-x-4">
                <div class="col-span-4">
                    Minimum Margin per Sale (just PROTECT)
                </div>
                <div class="bg-slate-600 rounded-md">
                    <TextField 
                        v-model="margin" 
                        density="compact"
                        variant="flat"
                        hide-details>
                        <template #append-inner>£</template>
                    </TextField>
                </div>
                <div class="p-1 h-10 flex items-center">
                </div>
                <div class="p-1 h-10 flex items-center">
                </div>
                <div class="rounded p-1 h-10 flex items-center justify-center">
                    <div class="w-0.5 h-[150%] bg-slate-300 -top-1"></div>
                    <div class="border-l-2 border-b-2 w-3 h-3 absolute -rotate-45 bottom-0"></div>
                </div>
                <div class="rounded p-1 h-10 flex items-center justify-center">
                    <div class="w-0.5 h-[150%] bg-slate-300 -top-1"></div>
                    <div class="border-l-2 border-b-2 w-3 h-3 absolute -rotate-45 bottom-0"></div>
                </div>
                <div class="rounded p-1 h-10 flex items-center justify-center">
                    <div class="w-0.5 h-[150%] bg-slate-300 -top-1"></div>
                    <div class="border-l-2 border-b-2 w-3 h-3 absolute -rotate-45 bottom-0"></div>
                </div>
            </div>


            <div class="grid grid-cols-10 items-center gap-x-4">
                <div class="col-span-4 font-semibold">
                    Projected Vehicle Sales
                </div>
                <div class="p-1 h-10 flex items-center">
                </div>
                <div class="text-right font-semibold">
                    {{ comma( monthlyVehicleSales, 0 ) }}
                </div>
                <div class="text-right font-semibold">
                    {{ comma( yearlyVehicleSales, 0 ) }}
                </div>
                <div class="bg-slate-100 rounded p-1 h-10 flex items-center justify-end">
                    {{ comma( Math.ceil( yearlyVehicleSales * 0.33 ), 0 ) }}
                </div>
                <div class="bg-slate-100 rounded p-1 h-10 flex items-center justify-end">
                    {{ comma( Math.ceil( yearlyVehicleSales * 0.66 ), 0 ) }}
                </div>
                <div class="bg-slate-100 rounded p-1 h-10 flex items-center justify-end">
                    {{ comma( Math.ceil( yearlyVehicleSales ), 0 ) }}
                </div>
            </div>


            <div class="grid grid-cols-10 items-center gap-x-4">
                <div class="col-span-4 font-semibold">
                    Projected PROTECT Contracts Sold
                </div>
                <div class="p-1 h-10 flex items-center">
                </div>
                <div class="text-right font-semibold">
                    {{ comma( monthlyProtectSales, 0 ) }}
                </div>
                <div class="text-right font-semibold">
                    {{ comma( yearlyProtectSales, 0 ) }}
                </div>
                <div class="bg-slate-100 rounded p-1 h-10 flex items-center justify-end">
                    {{ comma( Math.ceil( yearlyProtectSales * 0.33 ), 0 ) }}
                </div>
                <div class="bg-slate-100 rounded p-1 h-10 flex items-center justify-end">
                    {{ comma( Math.ceil( yearlyProtectSales * 0.66 ), 0 ) }}
                </div>
                <div class="bg-slate-100 rounded p-1 h-10 flex items-center justify-end">
                    {{ comma( yearlyProtectSales, 0 ) }}
                </div>
            </div>


            <div class="grid grid-cols-10 items-center gap-x-4 border-t-2 pt-4 mt-2 font-semibold text-emerald-800 text-lg">
                <div class="col-span-4">
                    Total Gross Profit
                </div>
                <div>
                </div>
                <div class="justify-end p-1 h-10 flex items-center">
                    {{ formatNumber( monthlyProtectRevenue, '£' ) }}
                </div>
                <div class="justify-end p-1 h-10 flex items-center">
                    {{ formatNumber( yearlyProtectRevenue, '£' ) }}
                </div>
                <div class="justify-end bg-slate-100 rounded p-1 h-10 flex items-center">
                    {{ formatNumber( yearlyProtectRevenue * 0.33, '£' ) }}
                </div>
                <div class="justify-end bg-slate-100 rounded p-1 h-10 flex items-center">
                    {{ formatNumber( yearlyProtectRevenue * 0.66, '£' ) }}
                </div>
                <div class="justify-end bg-slate-100 rounded p-1 h-10 flex items-center">
                    {{ formatNumber( yearlyProtectRevenue, '£' ) }}
                </div>
            </div>


            <div class="grid grid-cols-10 items-center gap-x-4 font-semibold text-emerald-800 text-lg">
                <div class="col-span-4 py-1 h-10 flex items-center">
                    Share of Gross Profit 
                </div>
                <div class="justify-center bg-emerald-600 text-white rounded p-1 h-10 flex items-center">
                    {{ comma( shareOfRevenue * 100 ) }}%
                </div>
                <div class="justify-end bg-emerald-200 text-emerald-700 rounded p-1 h-10 flex items-center">
                    {{ formatNumber( monthlyResellerRevenue, '£' ) }}
                </div>
                <div class="justify-end bg-emerald-200 text-emerald-700 rounded p-1 h-10 flex items-center">
                    {{ formatNumber( yearlyResellerRevenue, '£' ) }}
                </div>
                <div class="justify-end bg-emerald-500 text-white rounded p-1 h-10 flex items-center">
                    {{ formatNumber( yearlyResellerRevenue * 0.33, '£' ) }}
                </div>
                <div class="justify-end bg-emerald-500 text-white rounded p-1 h-10 flex items-center">
                    {{ formatNumber( yearlyResellerRevenue * 0.66, '£' ) }}
                </div>
                <div class="justify-end bg-emerald-500 text-white rounded p-1 h-10 flex items-center">
                    {{ formatNumber( yearlyResellerRevenue, '£' ) }}
                </div>
            </div>
           
        </div>
    </Page>

</template>